/**
 * Learning Plan Page for trAInedup.ai
 * Displays user's personalized learning plan from skills assessment
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, ArrowLeft, RefreshCw, AlertCircle } from 'lucide-react';
import LearningPlanDisplay from '../components/assessment/LearningPlanDisplay';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { ROUTES } from '../utils/constants';
import { Layout } from '../components/common/Layout';

const LearningPlanPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLearningPlan, setHasLearningPlan] = useState(false);

  useEffect(() => {
    const loadUserLearningPlan = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const planId = userData.currentLearningPlanId;
          
          if (planId) {
            setCurrentPlanId(planId);
            setHasLearningPlan(true);
          } else {
            setHasLearningPlan(false);
          }
        }
      } catch (error) {
        console.error('Error loading learning plan:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserLearningPlan();
  }, [user]);

  if (isLoading) {
    return (
      <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading your learning plan...</p>
            </div>
          </div>
        </div>
      </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(ROUTES.dashboard)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Dashboard"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
                  <Brain className="w-8 h-8 text-blue-500" />
                  <span>My Learning Plan</span>
                </h1>
                <p className="text-gray-600 mt-1">Your personalized AI learning journey</p>
              </div>
            </div>
            
            {hasLearningPlan && (
              <button
                onClick={() => navigate('/skills-assessment')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Retake Assessment</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hasLearningPlan ? (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center max-w-2xl mx-auto">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                No Learning Plan Yet
              </h2>
              <p className="text-gray-600 mb-6">
                You haven't completed a skills assessment yet. Take our AI skills assessment to get a personalized learning plan tailored to your goals and experience level.
              </p>
              <button
                onClick={() => navigate('/skills-assessment')}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-lg hover:from-blue-600 hover:to-teal-600 transition-all duration-200 font-semibold"
              >
                Take Skills Assessment
              </button>
            </div>
          </div>
        ) : (
          <LearningPlanDisplay planId={currentPlanId || undefined} />
        )}
      </div>
    </div>
    </Layout>
  );
};

export default LearningPlanPage;

